import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Eye, KeyRound, PartyPopper, ShieldAlert, Users } from "lucide-react";

import { ErrorApi } from "../api/cliente.ts";
import { usarAceptarInvitacion } from "../ganchos/datos.ts";
import { nombreRol } from "../sesion/permisos.ts";
import { MarcaCompleta } from "../componentes/Marca.tsx";
import { PanelPublico, type Argumento } from "../componentes/PanelPublico.tsx";
import { Aviso, Boton, Campo, Tarjeta } from "../componentes/ui.tsx";

const MINIMO = 12;

/**
 * Lo que necesita saber alguien que llega por un enlace.
 *
 * NO es el argumento comercial de `/entrar`: a esa pantalla llega quien ya es
 * cliente. Aquí llega alguien a quien acaban de dar acceso y que puede no haber
 * oído hablar de esto nunca — sus preguntas son otras. Qué es, qué va a ver, y
 * quién puede leer la contraseña que está a punto de escribir.
 */
const ARGUMENTOS: Argumento[] = [
  {
    icono: Eye,
    titulo: "Vas a ver los pagos que entran",
    texto:
      "4drando lee las alertas del banco y las cruza con lo que cada cliente debe. Tú confirmas de quién es un pago cuando el sistema no puede saberlo solo.",
  },
  {
    icono: Users,
    titulo: "Entras a la cuenta de quien te invitó",
    texto:
      "No es una cuenta tuya aparte: entras a la de tu empresa, con el rol que te dieron. Puedes ver quién más tiene acceso.",
  },
  {
    icono: KeyRound,
    titulo: "La contraseña la eliges tú",
    texto:
      "Nadie la conoce, ni quien te invitó ni nosotros: se guarda una huella suya, no la contraseña. Este enlace sirve una vez y caduca.",
  },
];

/**
 * Pantalla pública: acepta el enlace de invitación o de restablecimiento y deja
 * la contraseña puesta.
 *
 * El token llega en la URL porque el enlace es el único canal que hay. No se
 * guarda en ninguna parte: se usa una vez y muere con la pestaña.
 */
export function AceptarInvitacion() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const aceptar = usarAceptarInvitacion();

  const [contrasena, setContrasena] = useState("");
  const [repetida, setRepetida] = useState("");

  const noCoincide = repetida.length > 0 && contrasena !== repetida;
  const corta = contrasena.length > 0 && contrasena.length < MINIMO;

  return (
    // El mismo split que `/entrar`, y por una razón que no es de ancho: era la
    // única pantalla pública sin una palabra sobre qué es esto, y la ve
    // precisamente quien nunca lo ha usado. Que además llene la pantalla es una
    // consecuencia, no el motivo.
    <div className="grid min-h-dvh lg:grid-cols-2">
      <PanelPublico
        titular="Te dieron acceso a 4drando."
        entrada="Pon una contraseña y entras. Toma menos de un minuto, y desde ahí verás los pagos de tu empresa a medida que el banco los reporta."
        argumentos={ARGUMENTOS}
        pie="Si no esperabas este correo, no pongas ninguna contraseña: avisa a quien administra la cuenta."
      />

      <main className="flex items-center justify-center px-5 py-12 sm:px-10">
        <div className="w-full max-w-md space-y-8 subir">
          <div className="lg:hidden">
            <MarcaCompleta />
          </div>

        {!token ? (
          <Aviso tono="peligro" titulo="Este enlace está incompleto" icono={ShieldAlert}>
            Falta el código de la invitación. Vuelve a abrir el enlace tal como te lo enviaron, sin
            recortarlo.
          </Aviso>
        ) : aceptar.isSuccess ? (
          <Tarjeta className="space-y-5 p-7 text-center">
            <span className="mx-auto grid size-14 place-items-center rounded-2xl bg-exito-suave text-exito-tinta">
              <PartyPopper className="size-6" aria-hidden />
            </span>
            <div className="space-y-1.5">
              <h1 className="text-xl font-semibold tracking-tight">Ya tienes acceso</h1>
              <p className="text-sm text-tenue">
                Entraste como <strong className="text-tinta">{nombreRol(aceptar.data.rol)}</strong>.
                Usa tu correo y la contraseña que acabas de poner.
              </p>
            </div>
            <Link to="/">
              <Boton variante="primario" bloque icono={ArrowRight}>
                Ir a entrar
              </Boton>
            </Link>
          </Tarjeta>
        ) : (
          <Tarjeta className="space-y-6 p-7">
            <div className="space-y-1.5">
              <h1 className="text-xl font-semibold tracking-tight">Pon tu contraseña</h1>
              <p className="text-sm leading-relaxed text-tenue">
                Nadie más la va a conocer, ni siquiera quien te invitó. Si la pierdes, se genera un
                enlace nuevo — no se recupera la anterior.
              </p>
            </div>

            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (corta || noCoincide) return;
                aceptar.mutate({ token, contrasena });
              }}
            >
              <Campo
                etiqueta="Contraseña"
                type="password"
                icono={KeyRound}
                autoComplete="new-password"
                required
                minLength={MINIMO}
                value={contrasena}
                onChange={(e) => setContrasena(e.target.value)}
                ayuda={`Mínimo ${MINIMO} caracteres. Una frase que solo tú dirías funciona mejor que una palabra rara.`}
                error={corta ? `Le faltan ${MINIMO - contrasena.length} caracteres.` : undefined}
              />
              <Campo
                etiqueta="Repítela"
                type="password"
                autoComplete="new-password"
                required
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                error={noCoincide ? "No coinciden." : undefined}
              />

              {aceptar.error && (
                <Aviso titulo="No se pudo aceptar">
                  {aceptar.error instanceof ErrorApi
                    ? aceptar.error.message
                    : "Error al hablar con 4drando."}
                </Aviso>
              )}

              <Boton
                type="submit"
                variante="primario"
                tamano="lg"
                bloque
                cargando={aceptar.isPending}
                disabled={contrasena.length < MINIMO || noCoincide}
              >
                Activar mi acceso
              </Boton>
            </form>
          </Tarjeta>
        )}
        </div>
      </main>
    </div>
  );
}
